import { zodResolver } from "@hookform/resolvers/zod";
import { IMultilingualContent, IParentTicketForm, IParentTicketItem, ISimpleEventDetail, Phase, PriceType, SupportedLanguage } from "interfaces";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { ParentTicketSchema } from "../schema";
import { HttpError, useOne } from "@refinedev/core";
import { LANGUAGES, PRICE_TYPES, SELLING_TYPE } from "utills/constants";
import { get } from "lodash";
import { Label } from "components/label";
import Select from "components/select";
import Input from "components/input";
import { TextEditor } from "components/textEditor";
import { SwitchButton } from "components/switchButton";
import { RightSideModal } from "components/rightSideModal";
import Button from "components/button";
import { IModalProps } from "components/modal";
import { useEffect } from "react";
import moment from "moment";
import { PlusCircleIcon, TrashIcon } from "@heroicons/react/outline";

interface ParentTicketFormProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  onSubmit: (data: IParentTicketForm) => void;
  defaultValues?: IParentTicketItem | null;
}

interface IAttendantTemplateField {
  id: string;
  template_id: string;
  name: IMultilingualContent;
}

const DEFAULT_TEMPLATE = [{
  template_id: "",
  name: {
    en: "",
    vi: ""
  }
}];

const DEFAULT_TICKET_VALUE = {
  type_name: {
    en: "",
    vi: ""
  },
  short_description: {
    en: "",
    vi: ""
  },
  price_type: PriceType.PAID,
  original_price: 0,
  price: 0,
  sell_from: moment().startOf("day").format("YYYY-MM-DD HH:mm:ss"),
  sell_to: moment().endOf("day").format("YYYY-MM-DD HH:mm:ss"),
  is_soldout: false,
  is_active: true,
  ticket_phase: "",
  hide_in_ui: false
}

const getDefaultTemplate = (defaultValues: ParentTicketFormProps["defaultValues"] ) => {
  return {
    category: defaultValues?.category ?? {
      en: "",
      vi: ""
    },
    selling_min_qty: defaultValues?.selling_min_qty ?? 1,
    selling_max_qty: defaultValues?.selling_max_qty ?? 10,
    selling_type: defaultValues?.selling_type ?? "multiple",
    is_package: defaultValues?.is_package ?? false,
    attendant_template: defaultValues?.attendant_template ?? DEFAULT_TEMPLATE,
  }
}

export const ParentTicketForm = ({title, open, onClose, eventDetailData, defaultValues, onSubmit}: ParentTicketFormProps) => {
  const {
    control,
    handleSubmit,
    register,
    watch,
    reset,
    trigger,
    formState: { errors },
  } = useForm<IParentTicketForm, HttpError, IParentTicketForm>({
    defaultValues: getDefaultTemplate(defaultValues),
    resolver: (values, context, options) => {
      const createResolver = zodResolver(
        ParentTicketSchema(eventDetailData)
      );
      return createResolver(values, context, options);
    },
  });

  const { data } = useOne({
    resource: `content/attendant-templates/events`,
    id: eventDetailData.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventDetailData.id,
    } as any,
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "attendant_template"
  });

  useEffect(() => {
    if (open) {
      reset(getDefaultTemplate(defaultValues));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])


  const _submit = (data: IParentTicketForm) => {
    onSubmit(data);
  }

  const templatesDataArr = (data?.data ?? []).map((template: any) => ({
    label: get(template, "attendant_template_name", ""),
    value: get(template, "id", "")
  }))

  const firstLanguage = eventDetailData.languages.includes(SupportedLanguage.EN) ? "en" : eventDetailData.languages[0] ?? 'vi';


  return (
    <RightSideModal title={title ?? ""} open={open} onClose={onClose} footer={(
      <div className="w-full flex flex-col sm:flex-row gap-4 items-center">
        <Button 
          className="w-full bg-white border-none text-brand-500 ring-1 ring-inset ring-brand-500 hover:bg-gray-50 active:bg-gray-100" 
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button className="w-full uppercase" onClick={handleSubmit(_submit)}>Save</Button>
      </div>
    )}>
      <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4 p-4 sm:px-6">
        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">Ticket Info</p>
        </div>
        <div className="col-span-full">
          <Label
            required
            id={`category_${eventDetailData.languages[0] ?? "en"}`}
            name="Category"
          />
          <div className="flex flex-col gap-y-4">
            {eventDetailData.languages.map((lang: SupportedLanguage) => (
              <div key={lang}>
                <div className="relative" key={lang}>
                  <Input
                    id={`category_${lang}`}
                    type="text"
                    className="pr-11"
                    placeholder={`Cateogry (${lang})`}
                    {...register(`category.${lang}`)}
                    error={get(
                      errors,
                      `category.${lang}.message`,
                      ""
                    )}
                  />
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="absolute top-1/2 right-3 -translate-y-1/2"
                  />
                </div>
                {get(
                  errors,
                  `category.${lang}`,
                  ""
                ) ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(
                        errors,
                        `category.${lang}.message`,
                        ""
                      )}
                    </span>
                  </p>
                ) : null}
              </div>
            ))}
          </div>
        </div>
        <div className="sm:col-span-3">
          <Label
            id="selling_min_qty"
            name="Select min quantity"
            required
          />
          <Input
            id="selling_min_qty"
            type="number"
            min={0}
            placeholder={`Select min quantity`}
            {...register(`selling_min_qty`, {
              valueAsNumber: true
            })}
            error={get(
              errors,
              `selling_min_qty.message`,
              ""
            )}
          />
          {get(
            errors,
            `selling_min_qty`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `selling_min_qty.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="selling_max_qty"
            name="Select max quantity"
            required
          />
          <Input
            id="selling_max_qty"
            type="number"
            min={0}
            placeholder={`Select max quantity`}
            {...register(`selling_max_qty`, {
              valueAsNumber: true
            })}
            error={get(
              errors,
              `selling_max_qty.message`,
              ""
            )}
          />
          {get(
            errors,
            `selling_max_qty`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `selling_max_qty.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full">
          <Label
            id="selling_type"
            name="Selling Type"
            required
          />
          <Controller
            control={control}
            name="selling_type"
            render={({ field: { onChange, value = "", ref } }) => {
              return (
                <Select
                  key="selling_type"
                  inputRef={ref}
                  options={SELLING_TYPE}
                  id="selling_type"
                  placeholder="Select Selling Type"
                  defaultValue={
                    SELLING_TYPE.find(
                      (selected: { label: string; value: string }) =>
                        selected.value === value
                    ) ?? ""
                  }
                  value={SELLING_TYPE.find(
                    (selected: { label: string; value: string }) =>
                      selected.value === value
                  )}
                  onChange={(selected: { label: string; value: string }) => {
                    onChange(selected?.value);
                  }}
                  error={get(errors, `selling_type`, "")}
                />
              );
            }}
          />
          {get(
            errors,
            `selling_type`,
            ""
          ) ? (
            <p className="error-text">
              <span className="font-medium">
                {get(
                  errors,
                  `selling_type.message`,
                  ""
                )}
              </span>
            </p>
          ) : null}
        </div>
        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">Participant form Info</p>
        </div>
        <div className="col-span-full">
          {watch("is_package") ? (
            <button
              type="button"
              className="flex flex-row items-center w-fit text-sm bg-white text-brand-500 active:bg-gray-50"
              onClick={() => {
                append(DEFAULT_TEMPLATE);
                trigger("attendant_template");
              }}
            >
              <PlusCircleIcon className="w-5 h-5 mr-1.5" />{" "}
              <span className="shrink-0 text-sm">Add Participant forms</span>
            </button>
          ) : <></>}
          <div className="divide-y divide-gray-300">
            {fields.map((field: IAttendantTemplateField, index: number) => (
              <div key={field.id} className="py-4">
                {(watch("is_package") && fields.length > 1) ? (
                  <div className="col-span-full flex justify-end">
                    <button type="button" onClick={() => remove(index)}>
                      <TrashIcon className="w-6 h-6 text-brand-500" />
                    </button>
                  </div>
                ) : <></>}
                <div>
                  <Label
                    id={`attendant_template.${index}.template_id`}
                    name="Select Participant Form"
                    required
                  />
                  <Controller
                    control={control}
                    name={`attendant_template.${index}.template_id`}
                    render={({ field: { onChange, value = "", ref } }) => {
                      return (
                        <Select
                          key={`attendant_template.${index}.template_id`}
                          inputRef={ref}
                          options={templatesDataArr}
                          id="template_id"
                          placeholder="Select Template"
                          defaultValue={
                            templatesDataArr.find(
                              (selected: { label: string; value: string }) =>
                                selected.value === value
                            ) ?? ""
                          }
                          value={templatesDataArr.find(
                            (selected: { label: string; value: string }) =>
                              selected.value === value
                          )}
                          onChange={(selected: { label: string; value: string }) => {
                            onChange(selected?.value);
                            trigger(`attendant_template.${index}.template_id`);
                          }}
                          error={get(errors, `attendant_template.${index}.template_id`, "")}
                        />
                      );
                    }}
                  />
                  {get(
                    errors,
                    `attendant_template.${index}.template_id`,
                    ""
                  ) ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(
                          errors,
                          `attendant_template.${index}.template_id.message`,
                          ""
                        )}
                      </span>
                    </p>
                  ) : null}
                </div>
                {watch("is_package") ? (
                  <div className="mt-4">
                    <Label
                      id={`attendant_template.${index}.name`}
                      name="Form name displayed"
                    />
                    <div className="flex flex-col gap-y-4">
                      {eventDetailData.languages.map((lang: SupportedLanguage) => (
                        <div key={lang}>
                          <div className="relative">
                            <Input
                              id={`attendant_template.${index}.name.${lang}`}
                              type="text"
                              className="pr-11"
                              placeholder={`Form name displayed (${lang})`}
                              {...register(`attendant_template.${index}.name.${lang}`)}
                              error={get(
                                errors,
                                `attendant_template.${index}.name.${lang}.message`,
                                ""
                              )}
                            />
                            <img
                              src={`/images/flags/circle-${lang}.png`}
                              alt="Language Icon"
                              className="absolute top-1/2 right-3 -translate-y-1/2"
                            />
                          </div>
                          {get(
                            errors,
                            `attendant_template.${index}.name.${lang}`,
                            ""
                          ) ? (
                            <p className="error-text">
                              <span className="font-medium">
                                {get(
                                  errors,
                                  `attendant_template.${index}.name.${lang}.message`,
                                  ""
                                )}
                              </span>
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <></>}
              </div>
            ))}
            {get(
              errors,
              `attendant_template`,
              ""
            ) ? (
              <p className="error-text">
                <span className="font-medium">
                  {get(
                    errors,
                    `attendant_template.root.message`,
                    ""
                  )}
                </span>
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </RightSideModal>
  )
}